import { motion } from "framer-motion";

const CHAPTERS = [
  {
    n: "01",
    title: "Restore Trust Through Accountability and Transparency",
    quote: "This is your government. You deserve the truth.",
    text: "Establish an Independent Forensic Audit and Accountability Commission to conduct a detailed comprehensive review of Tribal finances beginning in 2015 — government, major contracts, Section 17 corporations, Four Bears Casino, the People's Fund, procurement, and major expenditures.",
  },
  {
    n: "02",
    title: "Return Power to the People",
    quote: "Your voice should never end on Election Day.",
    text: "Constitutional reforms that strengthen citizen participation: separation of powers, election of judges, recall elections, reasonable term limits, referendum rights, citizen initiatives, absentee and off-reservation voting, and election reforms.",
  },
  {
    n: "03",
    title: "Open Government Through Citizen Participation",
    quote: "You deserve a seat at the table.",
    text: "Create a standing Budget and Finance Committee, and appoint two citizen members-at-large to every standing committee of the Tribal Business Council — so the people help shape decisions before they are made.",
  },
  {
    n: "04",
    title: "Build a Professional Government",
    quote: "Hire the best people — not the best-connected people.",
    text: "Rescind Resolution 93-33-JJR and replace Council-controlled hiring with a professional, merit-based personnel system. Qualified employees, stable programs, fair government will create a hierarchy of competence.",
  },
  {
    n: "05",
    title: "Put Families First",
    quote: "People should always come before projects.",
    text: "Prioritize increased investment in housing, home ownership, neighborhood infrastructure, elder and veteran housing, and home rehabilitation — development that improves the mental, physical and spiritual well-being of our Tribal members.",
  },
  {
    n: "06",
    title: "Restore Four Bears Casino as an Economic Engine",
    quote: "This is your casino. Its success should benefit your family.",
    text: "An independent operational and financial review to improve profitability, strengthen internal controls, and eliminate wasteful spending — with proven gaming professionals at the helm.",
  },
  {
    n: "07",
    title: "Invest in Our Own Economy",
    quote: "Our first investment should always be here at home.",
    text: "Commission an independent feasibility study on acquiring and operating mature oil wells on Tribal lands, and extend their productive life for our allottees — in an effort to provide a dependable reservation economy for future generations.",
  },
  {
    n: "08",
    title: "Protect the People's Fund and Future Generations",
    quote: "Protected for your children and grandchildren.",
    text: "Strengthen the long-term security of the People's Fund and ensure any proposed changes are decided by the people through referendum — as its founding document requires.",
  },
  {
    n: "09",
    title: "No Tribal Member Left Behind",
    quote: "Compassion should be based on need — not politics.",
    text: "A permanent Life and Limb Assistance Policy with clear, objective standards for emergency help with catastrophic illness, injury, fires, funerals, and genuine hardship. Same hardship, same opportunity.",
  },
  {
    n: "10",
    title: "Preserve Our Culture and Invest in Our Youth",
    quote: "This is your heritage. Together, we will preserve it.",
    text: "Make cultural preservation, language revitalization, education, and youth leadership permanent priorities — including a Tribal Youth Council representing every community.",
  },
  {
    n: "11",
    title: "Strengthen Public Safety and Protect Our Communities",
    quote: "Every family deserves to feel safe.",
    text: "Strengthen cooperation among Tribal, Federal, State, and local law enforcement to combat drug trafficking, improve investigations, support families affected by MMIW, and improve emergency response.",
  },
  {
    n: "12",
    title: "Care for Our Elders, Veterans, and Families",
    quote: "This is your Nation. We honor those who built it.",
    text: "Prioritize services for our elders, veterans and families, strengthen on-Reservation emergency medical services, and make sure government resources reach those that need them most.",
  },
  {
    n: "13",
    title: "Protect Patient Choice and Tribal Sovereignty in Medical Cannabis",
    quote: "Compassion, medicine, and responsibility.",
    text: "Work with healthcare professionals, law enforcement, and the community on a responsible medical cannabis program — physician oversight, patient safety, secure licensing, and strong safeguards against abuse.",
  },
  {
    n: "14",
    title: "Honor the Rights of Original Allottees",
    quote: "Justice begins by listening to the families most affected.",
    text: "Appoint a Lakebed Allottees Advisory Committee to review the history and ownership of lakebed mineral interests and recommend a fair path forward.",
  },
  {
    n: "15",
    title: "Protect Our Land — No Data Centers on the Fort Berthold Reservation",
    quote: "This is your land. We have a responsibility to protect it.",
    text: "Oppose industrial-scale data centers on the Reservation, rescind existing authorizations, and protect our water, affordable electricity, agricultural economy, and the quiet enjoyment of our lands.",
  },
  {
    n: "16",
    title: "Protect Our Water and Build the Infrastructure for Our Future",
    quote: "This is your water. It is one of our greatest responsibilities.",
    text: "Quantify and protect every water right of the MHA Nation, and build a modern water system for the West Segment first — the foundation of a Reservation-wide water strategy.",
  },
];

export default function Platform() {
  return (
    <section id="platform" className="relative bg-navy noise pt-12 md:pt-16 pb-24 md:pb-36 px-5 md:px-10 overflow-hidden" data-testid="platform-section">
      <div className="max-w-6xl mx-auto">
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="text-xs uppercase tracking-[0.3em] font-semibold text-gold mb-6"
        >
          The Platform
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-serif text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-[#F8F9FA] mb-6"
        >
          Sixteen commitments.
          <span className="block italic font-medium text-white/85">One shared vision.</span>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="font-serif italic text-xl md:text-2xl text-gold mb-16 md:mb-24 max-w-2xl"
        >
          "This is your government. It should always answer to you."
        </motion.p>

        <div className="divide-y divide-white/10 border-y border-white/10">
          {CHAPTERS.map((c, i) => (
            <motion.article
              key={c.n}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="group relative grid md:grid-cols-12 gap-4 md:gap-8 py-10 md:py-14 items-start"
              data-testid={`platform-chapter-${c.n}`}
            >
              <span
                aria-hidden="true"
                className="md:col-span-3 font-serif font-black text-7xl md:text-8xl leading-none text-white group-hover:text-gold transition-colors duration-700 select-none"
              >
                {c.n}
              </span>
              <div className="md:col-span-9">
                <h3 className="font-sans text-xl md:text-2xl font-bold tracking-tight text-[#F8F9FA] mb-2 group-hover:text-gold transition-colors duration-500">
                  {c.title}
                </h3>
                <p className="font-serif italic text-base md:text-lg text-gold mb-3">"{c.quote}"</p>
                <p className="text-white/90 text-base md:text-lg leading-relaxed max-w-2xl">{c.text}</p>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
